import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo, useRef, useState } from "react";
import { IconContentCopy, IconPlus, IconRefresh, IconSuspended } from "@/assets/icons";
import { black, blue, state } from "@/theme/colors";
import { TLearnerForm } from "@/features/learner/types/learnerForm";
import { LearnerFormTitle } from "@/features/learner/components/learner-form/LearnerFormTitle";
import { useParams } from "react-router-dom";
import { formatLearnerAccountId } from "@/features/learner/utils/formatId";
import { useMe } from "@/api/services/main/auth";
import { issueAccountApi, resetPasswordApi, suspendAccountApi } from "@/api/services/main/learner";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, COPY_SUCCESS_MESSAGE, COPY_FAILED_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import dayjs from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { IconCopy } from "@/assets/icons/IconCopy";
import { USER_ACTIVE_STATUS, USER_TYPE } from "@/types/enum";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { useDialog } from "@/hooks/useDialog";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

type TLearnerAccountInfoProps = {
  data?: Partial<TLearnerForm>;
  disabledAccountBtn?: boolean;
  industryHasNoSubjects?: boolean;
  // Only the detail page surfaces the "copy all login info" action.
  isDetailPage?: boolean;
};

type TIssuedAccountData = {
  redirectUrl: string;
  password: string;
  accountIssuedAt?: string;
  deactivatedAt?: string | null;
};

export const LearnerAccountInfo: FC<TLearnerAccountInfoProps> = ({ data, disabledAccountBtn, industryHasNoSubjects, isDetailPage }) => {
  const { data: meResult } = useMe();
  const { learnerId } = useParams();
  const queryClient = useQueryClient();
  const { userType } = useInitSetup();
  const isSupportUser = userType === USER_TYPE.SUPPORT_USER;
  const [isLoadingIssue, setIsLoadingIssue] = useState(false);
  const [isIssued, setIsIssued] = useState(false);
  const [isLoadingSuspend, setIsLoadingSuspend] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [isLoadingResetPassword, setIsLoadingResetPassword] = useState(false);
  const [issuedData, setIssuedData] = useState<TIssuedAccountData | null>(null);
  const resetPasswordConfirm = useDialog();
  const copyLoginInfoConfirm = useDialog();
  const [, copyToClipboard] = useCopyToClipboard();

  const hasAccount = useMemo(
    () => !!(data?.userId && data?.user && !data?.user?.isArchive && data?.user?.activeStatus === USER_ACTIVE_STATUS.ACTIVE),
    [data],
  );

  // Account is active when: already has active account and not just suspended, OR just issued
  const isAccountActive = (hasAccount && !isSuspended) || isIssued;

  const handleIssueAccount = async () => {
    if (!learnerId) return;
    setIsLoadingIssue(true);
    try {
      const response = await issueAccountApi(learnerId);
      setIssuedData({
        redirectUrl: response.data.redirectUrl,
        password: response.data.password,
        accountIssuedAt: response.data?.accountIssuedAt,
        deactivatedAt: response.data?.deactivatedAt,
      });
      setIsSuspended(false);
      setIsIssued(true);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_DETAIL, learnerId] });
      toast.success(response.message ?? API_SUCCESS_MESSAGE);
    } catch (e) {
      showError(e);
    } finally {
      setIsLoadingIssue(false);
    }
  };

  const handleSuspendAccount = async () => {
    if (!learnerId) return;
    setIsLoadingSuspend(true);
    try {
      const response = await suspendAccountApi(learnerId);
      setIsIssued(false);
      setIsSuspended(true);
      setIssuedData((prev) => ({
        redirectUrl: "",
        password: "",
        accountIssuedAt: prev?.accountIssuedAt,
        deactivatedAt: response.data?.deactivatedAt,
      }));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_DETAIL, learnerId] });
      toast.success(response.message ?? API_SUCCESS_MESSAGE);
    } catch (e) {
      showError(e);
    } finally {
      setIsLoadingSuspend(false);
    }
  };

  const handleResetPassword = async () => {
    if (!learnerId) return;
    setIsLoadingResetPassword(true);
    try {
      const response = await resetPasswordApi(learnerId);
      setIssuedData((prev) => ({
        redirectUrl: response.data.redirectUrl,
        password: response.data.password,
        accountIssuedAt: prev?.accountIssuedAt,
      }));
      toast.success(response.message ?? API_SUCCESS_MESSAGE);
      return response.data;
    } catch (e) {
      showError(e);
    } finally {
      setIsLoadingResetPassword(false);
    }
  };

  const handleCopyToClipboard = async (text?: string) => {
    if (!text) return;
    const success = await copyToClipboard(text);
    if (success) {
      toast.success(COPY_SUCCESS_MESSAGE);
    } else {
      toast.error(COPY_FAILED_MESSAGE);
    }
  };

  const buildLoginInfoText = (url?: string, password?: string) =>
    [`アプリURL:\n${url ?? ""}`, `学習者ID:\n${displayLearnerAccountId ?? ""}`, `パスワード:\n${password ?? ""}`].join("\n\n");

  // When the plaintext password is already available we can copy straight away; otherwise the caller must
  // re-issue the password first (the copy button handles this via a confirm modal).
  const handleCopyLoginInfo = () => handleCopyToClipboard(buildLoginInfoText(displayRedirectUrl, issuedData?.password));

  // Ref blocks same-tick double-clicks; state drives the button's loading/disabled UI.
  const resetAndCopyInFlightRef = useRef(false);
  const [isCopyingAllLoginInfo, setIsCopyingAllLoginInfo] = useState(false);

  // Register the clipboard write synchronously (inside the click) with a Promise-backed ClipboardItem;
  // awaiting the reset API first would lose focus and fail with "Document is not focused".
  const handleResetAndCopyLoginInfo = () => {
    if (resetAndCopyInFlightRef.current) return;
    resetAndCopyInFlightRef.current = true;
    setIsCopyingAllLoginInfo(true);

    let resetSucceeded = true;
    const loginInfoBlob = handleResetPassword().then((result) => {
      if (!result) {
        resetSucceeded = false;
        throw new Error("Password reset failed");
      }
      return new Blob([buildLoginInfoText(result.redirectUrl, result.password)], { type: "text/plain" });
    });

    navigator.clipboard
      .write([new ClipboardItem({ "text/plain": loginInfoBlob })])
      .then(() => toast.success(COPY_SUCCESS_MESSAGE))
      .catch(() => {
        if (resetSucceeded) toast.error(COPY_FAILED_MESSAGE); // only genuine copy failures
      })
      .finally(() => {
        resetAndCopyInFlightRef.current = false;
        setIsCopyingAllLoginInfo(false);
      });
  };

  const displayRedirectUrl = issuedData ? issuedData.redirectUrl : hasAccount && !isSuspended ? (data?.redirectUrl ?? undefined) : undefined;
  const displayPassword = issuedData ? issuedData.password : hasAccount && !isSuspended ? "••••••••••" : undefined;
  const accountCreatedAt = data?.user?.accountIssuedAt ? data.user.accountIssuedAt : issuedData?.accountIssuedAt;
  const accountDeactivatedAt = data?.user?.deactivatedAt ? data.user.deactivatedAt : issuedData?.deactivatedAt;

  // Account is stopped when it was issued before but is now suspended/archived (and not just re-issued).
  const isAccountStopped =
    !isIssued &&
    (isSuspended ||
      !!data?.isArchive ||
      (!!data?.userId && !!data?.user && (data.user.isArchive || data.user.activeStatus !== USER_ACTIVE_STATUS.ACTIVE)));

  // Summary line shown under the section title. When the account is stopped/archived, the issuance date is
  // labelled "以前のアカウント発行日" and the stop date is shown; otherwise it stays "アカウント発行日".
  const ACCOUNT_DATE_PLACEHOLDER = "----/--/--";
  const formatAccountDate = (value?: string | null) => (value ? dayjs(value).format(DATE_FORMAT) : ACCOUNT_DATE_PLACEHOLDER);
  const accountInfoSummary = [
    `${isAccountStopped ? "以前のアカウント発行日" : "アカウント発行日"} ${formatAccountDate(accountCreatedAt || undefined)}`,
    isAccountStopped ? `アカウント停止日 ${formatAccountDate(accountDeactivatedAt)}` : null,
    `最終ログイン ${formatAccountDate(data?.lastLoginTime)}`,
  ]
    .filter(Boolean)
    .join("・");

  const isResetPasswordDisabled = disabledAccountBtn || !hasAccount || isLoadingResetPassword || isSupportUser;

  const displayLearnerAccountId = learnerId ? formatLearnerAccountId(meResult?.data?.rsoId, meResult?.data?.companyId, learnerId) : undefined;

  return (
    <>
      <InnerBox
        id="learner-account-section"
        title={
          <Box display="flex" flexDirection="column">
            <LearnerFormTitle text="学習アプリのアカウント管理" marginBottom="11px" />
            <Typography variant="body14Light" color={black[70]}>
              {accountInfoSummary}
            </Typography>
          </Box>
        }
        showDivider={false}
        gap={3}
        rightContent={
          <Stack direction="row" gap={2}>
            {industryHasNoSubjects ? (
              <Button variant="outline" size="sm" startIcon={<IconPlus width={16} height={16} />} disabled>
                アカウント発行
              </Button>
            ) : isAccountActive ? (
              <Button
                variant="danger"
                size="sm"
                color="error"
                startIcon={<IconSuspended />}
                onClick={handleSuspendAccount}
                disabled={disabledAccountBtn || isLoadingSuspend}
              >
                アカウント停止
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                startIcon={<IconPlus width={16} height={16} />}
                disabled={disabledAccountBtn || !learnerId || isLoadingIssue || !!data?.isArchive}
                onClick={handleIssueAccount}
              >
                アカウント発行
              </Button>
            )}
          </Stack>
        }
      >
        {industryHasNoSubjects ? (
          <Typography component="p" variant="body14" color={state.warning_1} textAlign="center" mt="7px">
            現在この分野の学習コンテンツは提供されていません。
          </Typography>
        ) : (
          <Grid container rowSpacing={2} columnSpacing={3} alignItems="flex-end">
            <Grid item xs={6}>
              <Stack spacing={1}>
                <Typography variant="cap12Light" color={black[70]}>
                  学習者ID
                </Typography>
                <Input
                  readonly
                  placeholder="学習者の保存後に発行できます"
                  value={displayLearnerAccountId}
                  clearable={false}
                  endAdornment={
                    displayLearnerAccountId && (
                      <Box
                        component="span"
                        sx={{ cursor: "pointer", pointerEvents: "auto", display: "flex" }}
                        onClick={() => handleCopyToClipboard(displayLearnerAccountId)}
                      >
                        <IconContentCopy />
                      </Box>
                    )
                  }
                />
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="cap12Light" color={black[70]}>
                    パスワード
                  </Typography>
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap={0.5}
                    onClick={isResetPasswordDisabled ? undefined : resetPasswordConfirm.show}
                    sx={{
                      color: isResetPasswordDisabled ? black[70] : blue[80],
                      cursor: isResetPasswordDisabled ? "not-allowed" : "pointer",
                      pointerEvents: isResetPasswordDisabled ? "none" : "auto",
                    }}
                  >
                    <IconRefresh />
                    <Typography variant="cap12Bold">パスワードの再発行</Typography>
                  </Stack>
                </Stack>
                <Input
                  readonly
                  placeholder="アカウントを発行してください"
                  value={displayPassword || ""}
                  endAdornment={
                    issuedData?.password && (
                      <Box
                        component="span"
                        sx={{ cursor: "pointer", pointerEvents: "auto", display: "flex" }}
                        onClick={() => handleCopyToClipboard(issuedData.password)}
                      >
                        <IconContentCopy />
                      </Box>
                    )
                  }
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Typography variant="cap12Light" color={black[70]}>
                  ログインURL
                </Typography>
                <Input
                  readonly
                  placeholder="アカウントを発行してください"
                  value={displayRedirectUrl || ""}
                  endAdornment={
                    displayRedirectUrl && (
                      <Box
                        component="span"
                        sx={{ cursor: "pointer", pointerEvents: "auto", display: "flex" }}
                        onClick={() => handleCopyToClipboard(displayRedirectUrl)}
                      >
                        <IconContentCopy />
                      </Box>
                    )
                  }
                />
              </Stack>
            </Grid>
            {isDetailPage && isAccountActive && (
              <Grid item xs={12} mt={1}>
                <Button
                  variant="outline"
                  size="sm"
                  startIcon={<IconCopy />}
                  isLoading={isCopyingAllLoginInfo}
                  onClick={
                    issuedData?.password
                      ? handleCopyLoginInfo
                      : () => {
                          copyLoginInfoConfirm.show();
                        }
                  }
                  sx={{ width: "100%" }}
                >
                  ログイン情報をまとめてコピー
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </InnerBox>
      <ConfirmSaveModal
        open={resetPasswordConfirm.open}
        onOk={() => {
          resetPasswordConfirm.confirm();
          handleResetPassword();
        }}
        onClose={resetPasswordConfirm.cancel}
      >
        <Typography>パスワードを再発行してよろしいですか（注意: 元のパスワードを上書きします）</Typography>
      </ConfirmSaveModal>
      <ConfirmSaveModal
        open={copyLoginInfoConfirm.open}
        onOk={() => {
          copyLoginInfoConfirm.confirm();
          handleResetAndCopyLoginInfo();
        }}
        onClose={copyLoginInfoConfirm.cancel}
      >
        <Typography>
          現在のパスワードを破棄し、再発行が必要です。すでに本人が利用中の場合はログアウトされますが、続行してもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </>
  );
};
